import { useEffect, useState } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Table, Input, Select, Button, Modal, Form, Row, Col, Card, Statistic, Tag,
  Typography, Space, Radio, Checkbox, InputNumber, DatePicker, App as AntApp,
  Flex, Popconfirm, Upload, Empty, Tooltip,
} from 'antd';
import {
  SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
  CopyOutlined, StopOutlined, PaperClipOutlined, UploadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { API_URL } from '../config';
import { hojeIso } from '../utils/dataFmt';
import { PROPS_DINHEIRO_BR } from '../utils/dinheiroInput';

const { Title, Text } = Typography;

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

const fmt = (v: number) =>
  'R$ ' + (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (s: string | null) => {
  if (!s) return '—';
  const [y, m, d] = s.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};

interface Bem {
  id: string; descricao: string; codigo_patrimonio: string | null;
  tipo: 'tangivel' | 'intangivel'; categoria: string | null;
  alocacao: string | null; fornecedor: string | null; modelo: string | null;
  data_aquisicao: string; valor_pago: string; valor_residual: string;
  vida_util_meses: number; metodo: string;
  data_baixa: string | null; motivo_baixa: string | null; valor_venda: string | null;
  observacoes: string | null; garantia_meses: number | null;
  depreciacao_acumulada: number; valor_contabil: number; dep_mensal: number;
  anexos_count: number;
  // FECHAMENTO-CC F3: custos de operação anuais e custo/dia de referência (21 d.u.)
  custos_operacao?: Record<string, number> | null; custo_operacao_mensal?: number; custo_dia?: number;
}
const CUSTOS_OPERACAO_CAMPOS: { key: string; label: string; sufixo: string; dica: string }[] = [
  { key: 'manutencao_pct_aa', label: 'Manutenção', sufixo: '% a.a.', dica: 'sobre o valor pago, por ano → linha Manutenção de Equipamentos' },
  { key: 'pecas_pct_aa',      label: 'Peças / desgaste', sufixo: '% a.a.', dica: 'sobre o valor pago, por ano → Manutenção de Equipamentos' },
  { key: 'seguro_pct_aa',     label: 'Seguro', sufixo: '% a.a.', dica: 'sobre o valor pago, por ano → Seguro de Equipamentos' },
  { key: 'ipva_pct_aa',       label: 'IPVA', sufixo: '% a.a.', dica: 'sobre o valor pago, por ano → IPVA / DPVAT / Licenciamento' },
  { key: 'licenciamento_aa',  label: 'Licenciamento', sufixo: 'R$ / ano', dica: 'valor fixo por ano → IPVA / DPVAT / Licenciamento' },
  { key: 'outros_aa',         label: 'Outros', sufixo: 'R$ / ano', dica: 'valor fixo por ano → Manutenção de Equipamentos' },
];

const CATEGORIAS_PRESET = [
  { label: 'Veículos',              vida: 60,  pctDep: 50,  tipo: 'tangivel',   alocacao: 'direta'   },
  { label: 'Barcos/Embarcações',    vida: 120, pctDep: 70,  tipo: 'tangivel',   alocacao: 'direta'   },
  { label: 'Equipamento Eletrônico', vida: 60,  pctDep: 75,  tipo: 'tangivel',   alocacao: 'direta'   },
  { label: 'Equipamento Mecânico',  vida: 120, pctDep: 100, tipo: 'tangivel',   alocacao: 'direta'   },
  { label: 'Escritório',            vida: 60,  pctDep: 80,  tipo: 'tangivel',   alocacao: 'indireta' },
  { label: 'Monitor',                vida: 60,  pctDep: 100, tipo: 'tangivel',   alocacao: 'indireta' },
  { label: 'Copa/Cozinha',          vida: 120, pctDep: 80,  tipo: 'tangivel',   alocacao: 'indireta' },
  { label: 'Móveis',                 vida: 120, pctDep: 80,  tipo: 'tangivel',   alocacao: 'indireta' },
  { label: 'Edificações',            vida: 300, pctDep: 80,  tipo: 'tangivel',   alocacao: 'indireta' },
  { label: 'Software',               vida: 60,  pctDep: 100, tipo: 'intangivel', alocacao: 'direta'   },
];

function PatrimonioInner() {
  const { message } = AntApp.useApp();
  const [bens, setBens] = useState<Bem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState<'ativos' | 'baixados' | 'todos'>('ativos');
  const [filtroTipo, setFiltroTipo] = useState<'todos' | 'tangivel' | 'intangivel'>('todos');
  const [filtroCategoria, setFiltroCategoria] = useState<string>('todas');
  const [busca, setBusca] = useState('');
  const [editando, setEditando] = useState<Bem | null>(null);
  const [mostrarNovo, setMostrarNovo] = useState(false);
  const [baixando, setBaixando] = useState<Bem | null>(null);
  const [duplicandoBem, setDuplicandoBem] = useState<any | null>(null);

  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [filtroStatus, filtroTipo]);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const qs = `status=${filtroStatus}${filtroTipo !== 'todos' ? `&tipo=${filtroTipo}` : ''}`;
      const res = await axios.get(`${API_URL}/patrimonio?${qs}`, { headers: { Authorization: token } });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      setBens(data);
    } finally { setLoading(false); }
  }

  async function executarDuplicacao(bemId: string, incluirAnexos: boolean) {
    try {
      const token = await getToken();
      await axios.post(
        `${API_URL}/patrimonio/${bemId}/duplicar?incluir_anexos=${incluirAnexos ? 'true' : 'false'}`,
        {}, { headers: { Authorization: token } }
      );
      message.success('Bem duplicado.');
      await carregar();
      setDuplicandoBem(null);
    } catch (e: any) {
      message.error('Erro ao duplicar: ' + (e.response?.data?.erro || e.response?.data?.error || e.message));
    }
  }

  async function excluir(id: string) {
    try {
      const token = await getToken();
      await axios.delete(`${API_URL}/patrimonio/${id}`, { headers: { Authorization: token } });
      message.success('Bem excluído.');
      await carregar();
    } catch (e: any) { message.error('Erro: ' + e.message); }
  }

  const filtrados = bens.filter(b => {
    if (filtroCategoria !== 'todas' && b.categoria !== filtroCategoria) return false;
    if (!busca) return true;
    const q = busca.toLowerCase();
    return b.descricao.toLowerCase().includes(q)
            || (b.fornecedor || '').toLowerCase().includes(q)
            || (b.categoria || '').toLowerCase().includes(q)
            || (b.codigo_patrimonio || '').toLowerCase().includes(q);
  });

  const totInvest = filtrados.reduce((s, b) => s + parseFloat(b.valor_pago), 0);
  const totContabil = filtrados.reduce((s, b) => s + b.valor_contabil, 0);
  const totDepMensal = filtrados.filter(b => !b.data_baixa).reduce((s, b) => s + b.dep_mensal, 0);
  const totTangiveis = filtrados.filter(b => b.tipo === 'tangivel' && !b.data_baixa).length;
  const totIntangiveis = filtrados.filter(b => b.tipo === 'intangivel' && !b.data_baixa).length;

  const cols: ColumnsType<Bem> = [
    {
      title: 'Cód.', dataIndex: 'codigo_patrimonio', key: 'cod', width: 70, align: 'center',
      sorter: (a, b) => {
        const ac = a.codigo_patrimonio || ''; const bc = b.codigo_patrimonio || '';
        const an = parseInt(ac, 10); const bn = parseInt(bc, 10);
        if (!isNaN(an) && !isNaN(bn)) return an - bn;
        return ac.localeCompare(bc);
      },
      render: (v) => v ? <Text style={{ color: '#6A7092', fontSize: 11, fontWeight: 500 }}>{v}</Text> : '—',
    },
    {
      title: 'Descrição', dataIndex: 'descricao', key: 'desc',
      sorter: (a, b) => a.descricao.localeCompare(b.descricao),
      render: (_, b) => (
        <div>
          <Text strong style={{ color: '#020D7F', fontSize: 12 }}>
            {b.descricao}
            {b.tipo === 'intangivel' && <Tag color="blue" style={{ marginLeft: 5, fontSize: 9 }}>INTAN</Tag>}
            {b.anexos_count > 0 && (
              <Text style={{ color: '#0a7f4a', fontSize: 10, marginLeft: 5 }} title={`${b.anexos_count} anexo(s)`}>
                <PaperClipOutlined /> {b.anexos_count}
              </Text>
            )}
          </Text>
          {b.modelo && (
            <div>
              <Text type="secondary" style={{ fontSize: 10 }}>{b.modelo} · {b.fornecedor}</Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Categoria', dataIndex: 'categoria', key: 'cat',
      sorter: (a, b) => (a.categoria || '').localeCompare(b.categoria || ''),
      render: (v) => v || '—',
    },
    {
      title: 'Aquisição', dataIndex: 'data_aquisicao', key: 'da', width: 100, align: 'center',
      sorter: (a, b) => a.data_aquisicao.localeCompare(b.data_aquisicao),
      render: (v) => <Text type="secondary" style={{ fontSize: 11 }}>{fmtDate(v)}</Text>,
    },
    {
      title: 'Valor pago', dataIndex: 'valor_pago', key: 'vp', width: 110, align: 'right',
      sorter: (a, b) => parseFloat(a.valor_pago) - parseFloat(b.valor_pago),
      render: (v) => fmt(parseFloat(v)),
    },
    {
      title: 'Residual', dataIndex: 'valor_residual', key: 'vr', width: 100, align: 'right',
      render: (v) => <Text type="secondary" style={{ fontSize: 11 }}>{fmt(parseFloat(v))}</Text>,
    },
    {
      title: 'Vida', dataIndex: 'vida_util_meses', key: 'vum', width: 60, align: 'center',
      render: (v) => <Text type="secondary" style={{ fontSize: 11 }}>{Math.round(v / 12)}a</Text>,
    },
    {
      title: 'Dep. mensal', dataIndex: 'dep_mensal', key: 'dm', width: 100, align: 'right',
      sorter: (a, b) => a.dep_mensal - b.dep_mensal,
      render: (v) => <Text style={{ color: '#B07500' }}>{fmt(v)}</Text>,
    },
    {
      title: <Tooltip title="Custo do bem por dia útil (depreciação + custos de operação ÷ 21). É o que cada contrato paga por dia de check-in do kit no fechamento de CC.">R$/dia</Tooltip>,
      dataIndex: 'custo_dia', key: 'cd', width: 90, align: 'right',
      sorter: (a, b) => (a.custo_dia || 0) - (b.custo_dia || 0),
      render: (v, b) => v ? <Text style={{ color: b.custos_operacao ? '#0a7f4a' : '#555' }}>{fmt(v)}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Valor contábil', dataIndex: 'valor_contabil', key: 'vc', width: 110, align: 'right',
      sorter: (a, b) => a.valor_contabil - b.valor_contabil,
      render: (v) => <Text strong>{fmt(v)}</Text>,
    },
    {
      title: 'Status', key: 'st', width: 100, align: 'center',
      render: (_, b) => b.data_baixa
        ? <Tag color="default">BAIXADO {fmtDate(b.data_baixa)}</Tag>
        : <Tag color="green">Ativo</Tag>,
    },
    {
      title: '', key: 'ac', width: 150, align: 'center', fixed: 'right',
      render: (_, b) => (
        <Space size={2}>
          <Button size="small" icon={<EditOutlined />} onClick={() => setEditando(b)} title="Editar" />
          <Button size="small" icon={<CopyOutlined />} onClick={() => setDuplicandoBem(b)} title="Duplicar" />
          {!b.data_baixa && (
            <Button size="small" icon={<StopOutlined />} onClick={() => setBaixando(b)}
              style={{ color: '#B07500', borderColor: '#B07500' }} title="Baixar" />
          )}
          <Popconfirm title="Excluir este bem? (ação definitiva)" onConfirm={() => excluir(b.id)}
            okText="Excluir" cancelText="Cancelar" okButtonProps={{ danger: true }}>
            <Button size="small" danger icon={<DeleteOutlined />} title="Excluir" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 1400, margin: '16px auto', padding: '0 24px' }}>
      <Flex align="center" gap={12} wrap="wrap" style={{ marginBottom: 14 }}>
        <Title level={4} style={{ margin: 0, color: '#020D7F' }}>Patrimônio</Title>
        <Radio.Group value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)} size="small">
          <Radio.Button value="ativos">Ativos</Radio.Button>
          <Radio.Button value="baixados">Baixados</Radio.Button>
          <Radio.Button value="todos">Todos</Radio.Button>
        </Radio.Group>
        <Select value={filtroTipo} onChange={setFiltroTipo} size="small" style={{ width: 160 }}
          options={[
            { label: 'Todos tipos', value: 'todos' },
            { label: 'Tangíveis (dep.)', value: 'tangivel' },
            { label: 'Intangíveis (amort.)', value: 'intangivel' },
          ]} />
        <Select value={filtroCategoria} onChange={setFiltroCategoria} size="small" style={{ width: 160 }}
          options={[
            { label: 'Todas categorias', value: 'todas' },
            ...Array.from(new Set(bens.map(b => b.categoria).filter(Boolean))).sort()
              .map(c => ({ label: c as string, value: c as string })),
          ]} />
        <Input placeholder="Buscar descrição, categoria, fornecedor..."
          value={busca} onChange={e => setBusca(e.target.value)}
          prefix={<SearchOutlined style={{ color: '#aaa' }} />}
          allowClear style={{ maxWidth: 320, flex: 1 }} />
        <Button type="primary" icon={<PlusOutlined />} style={{ marginLeft: 'auto' }} onClick={() => setMostrarNovo(true)}>
          Novo bem
        </Button>
      </Flex>

      <Row gutter={[10, 10]} style={{ marginBottom: 14 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Total investido" value={fmt(totInvest)} valueStyle={{ fontSize: 16, color: '#020D7F' }} />
            <Text type="secondary" style={{ fontSize: 10 }}>{filtrados.length} bens</Text>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Valor contábil" value={fmt(totContabil)} valueStyle={{ fontSize: 16, color: '#020D7F' }} />
            <Text type="secondary" style={{ fontSize: 10 }}>
              {totInvest > 0 ? `${(totContabil/totInvest*100).toFixed(0)}% do investido` : ''}
            </Text>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Depreciação mensal" value={fmt(totDepMensal)} valueStyle={{ fontSize: 16, color: '#B07500' }} />
            <Text type="secondary" style={{ fontSize: 10 }}>Anual: {fmt(totDepMensal*12)}</Text>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Composição" value={`${totTangiveis} + ${totIntangiveis}`} valueStyle={{ fontSize: 16, color: '#020D7F' }} />
            <Text type="secondary" style={{ fontSize: 10 }}>Tangíveis + Intangíveis</Text>
          </Card>
        </Col>
      </Row>

      <Table
        rowKey="id" columns={cols} dataSource={filtrados} loading={loading}
        size="small" pagination={{ pageSize: 50, showSizeChanger: true }}
        rowClassName={(r) => r.data_baixa ? 'row-fechado' : ''}
        locale={{ emptyText: <Empty description="Nenhum bem neste filtro." /> }}
        scroll={{ x: 'max-content' }}
      />

      {(editando || mostrarNovo) && (
        <FormBem bem={editando}
          onClose={() => { setEditando(null); setMostrarNovo(false); }}
          onSaved={async () => { await carregar(); setEditando(null); setMostrarNovo(false); }} />
      )}

      {baixando && (
        <ModalBaixar bem={baixando}
          onClose={() => setBaixando(null)}
          onSaved={async () => { await carregar(); setBaixando(null); }} />
      )}

      {duplicandoBem && (
        <ModalDuplicar bem={duplicandoBem}
          onClose={() => setDuplicandoBem(null)}
          onConfirmar={(incluirAnexos) => executarDuplicacao(duplicandoBem.id, incluirAnexos)} />
      )}
    </div>
  );
}

function ModalDuplicar({ bem, onClose, onConfirmar }: {
  bem: any; onClose: () => void; onConfirmar: (incluirAnexos: boolean) => void;
}) {
  const [incluirAnexos, setIncluirAnexos] = useState(false);
  const temAnexos = (bem.anexos_count || 0) > 0;
  return (
    <Modal
      open onCancel={onClose} title="Duplicar bem do patrimônio"
      onOk={() => onConfirmar(temAnexos && incluirAnexos)}
      okText="Duplicar" cancelText="Cancelar"
      okButtonProps={{ style: { background: '#7C3AED', borderColor: '#7C3AED' } }}
    >
      <Text style={{ fontSize: 12 }}>
        Será criada uma cópia de <strong>{bem.descricao}</strong> com todos os campos preservados.
        O código de patrimônio do novo bem fica vazio — você define depois.
      </Text>
      {temAnexos ? (
        <Card size="small" style={{ marginTop: 14, background: '#FAFBFF' }}>
          <Checkbox checked={incluirAnexos} onChange={e => setIncluirAnexos(e.target.checked)}>
            <Text strong style={{ fontSize: 12 }}>Copiar os {bem.anexos_count} anexo(s) também</Text>
            <div>
              <Text type="secondary" style={{ fontSize: 10 }}>Os arquivos são compartilhados — não duplicam storage.</Text>
            </div>
          </Checkbox>
        </Card>
      ) : (
        <div style={{ marginTop: 14, fontSize: 11, color: '#888', fontStyle: 'italic', padding: 8, background: '#F0F1F5', borderRadius: 4 }}>
          Este bem não tem anexos.
        </div>
      )}
    </Modal>
  );
}

interface Anexo {
  id: string; tipo: string; s3_key: string; nome_original: string;
  content_type: string | null; tamanho_bytes: number | null; descricao: string | null; criado_em: string;
}

function SecaoAnexos({ bemId }: { bemId: string }) {
  const { message, modal } = AntApp.useApp();
  const [anexos, setAnexos] = useState<Anexo[]>([]);
  const [uploading, setUploading] = useState(false);
  const [tipoAnexo, setTipoAnexo] = useState<'nf' | 'contrato' | 'manual' | 'foto' | 'outro'>('nf');

  useEffect(() => { carregar(); }, [bemId]); // eslint-disable-line

  async function carregar() {
    try {
      const token = await getToken();
      const res = await axios.get(`${API_URL}/patrimonio/${bemId}/anexos`, { headers: { Authorization: token } });
      setAnexos(res.data.body ? JSON.parse(res.data.body) : res.data);
    } catch {}
  }

  async function upload(file: File) {
    setUploading(true);
    try {
      const token = await getToken();
      const urlRes = await axios.post(`${API_URL}/patrimonio/${bemId}/anexo/upload-url`, {
        nome_original: file.name, content_type: file.type || 'application/octet-stream',
      }, { headers: { Authorization: token } });
      const { uploadUrl, s3Key } = urlRes.data.body ? JSON.parse(urlRes.data.body) : urlRes.data;
      await axios.put(uploadUrl, file, { headers: { 'Content-Type': file.type || 'application/octet-stream' } });
      await axios.post(`${API_URL}/patrimonio/${bemId}/anexo`, {
        tipo: tipoAnexo, s3_key: s3Key, nome_original: file.name,
        content_type: file.type, tamanho_bytes: file.size,
      }, { headers: { Authorization: token } });
      message.success('Anexo enviado.');
      await carregar();
    } catch (e: any) {
      message.error('Erro no upload: ' + e.message);
    } finally { setUploading(false); }
  }

  async function baixar(a: Anexo) {
    try {
      const token = await getToken();
      const res = await axios.get(`${API_URL}/patrimonio/${bemId}/anexo/${a.id}/download`, { headers: { Authorization: token } });
      const { downloadUrl } = res.data.body ? JSON.parse(res.data.body) : res.data;
      window.open(downloadUrl, '_blank');
    } catch {}
  }

  function excluirAnexo(a: Anexo) {
    modal.confirm({
      title: `Excluir anexo "${a.nome_original}"?`, okText: 'Excluir', cancelText: 'Cancelar', okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const token = await getToken();
          await axios.delete(`${API_URL}/patrimonio/${bemId}/anexo/${a.id}`, { headers: { Authorization: token } });
          await carregar();
        } catch {}
      },
    });
  }

  const tipoLabel: Record<string, string> = { nf: 'NF', contrato: 'Contrato', manual: 'Manual', foto: 'Foto', outro: 'Outro' };
  const tamKB = (b: number | null) => b ? (b < 1024*1024 ? `${(b/1024).toFixed(0)} KB` : `${(b/1024/1024).toFixed(1)} MB`) : '—';

  return (
    <div style={{ marginTop: 14, borderTop: '0.5px solid #f0f0f0', paddingTop: 12 }}>
      <Text strong style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 8 }}>
        Anexos ({anexos.length})
      </Text>
      <Flex gap={8} align="center" style={{ marginBottom: 10 }}>
        <Select value={tipoAnexo} onChange={(v) => setTipoAnexo(v)} size="small" style={{ width: 130 }}
          options={[
            { label: 'Nota Fiscal', value: 'nf' }, { label: 'Contrato', value: 'contrato' },
            { label: 'Manual', value: 'manual' }, { label: 'Foto', value: 'foto' },
            { label: 'Outro', value: 'outro' },
          ]} />
        <Upload
          customRequest={({ file, onSuccess }) => { upload(file as File).then(() => onSuccess?.({})); }}
          showUploadList={false} disabled={uploading}
        >
          <Button size="small" type="primary" ghost icon={<UploadOutlined />} loading={uploading}>
            Anexar arquivo
          </Button>
        </Upload>
        <Text type="secondary" style={{ fontSize: 10 }}>PDF, XML, JPG, PNG até 10MB</Text>
      </Flex>
      {anexos.length === 0 ? (
        <Text type="secondary" italic style={{ fontSize: 12 }}>Nenhum anexo ainda.</Text>
      ) : (
        <Card size="small" bodyStyle={{ padding: 0 }}>
          {anexos.map((a, i) => (
            <Flex key={a.id} align="center" gap={10} style={{
              padding: '8px 10px', fontSize: 12,
              borderBottom: i < anexos.length - 1 ? '0.5px solid #f0f0f0' : 'none',
            }}>
              <Tag color="blue" style={{ minWidth: 55, textAlign: 'center', margin: 0 }}>{tipoLabel[a.tipo] || a.tipo}</Tag>
              <Text strong style={{ flex: 1, fontSize: 12 }}>{a.nome_original}</Text>
              <Text type="secondary" style={{ fontSize: 10 }}>{tamKB(a.tamanho_bytes)}</Text>
              <Button size="small" type="primary" ghost onClick={() => baixar(a)}>Abrir</Button>
              <Button size="small" danger icon={<DeleteOutlined />} onClick={() => excluirAnexo(a)} />
            </Flex>
          ))}
        </Card>
      )}
    </div>
  );
}

function FormBem({ bem, onClose, onSaved }: { bem: Bem | null; onClose: () => void; onSaved: () => void }) {
  const { message } = AntApp.useApp();
  const initialPctDep = bem && parseFloat(bem.valor_pago) > 0
    ? ((1 - parseFloat(bem.valor_residual) / parseFloat(bem.valor_pago)) * 100).toFixed(2)
    : '';

  const [form, setForm] = useState<any>({
    codigo_patrimonio: bem?.codigo_patrimonio || '',
    descricao: bem?.descricao || '',
    tipo: bem?.tipo || 'tangivel',
    categoria: bem?.categoria || 'Equipamento',
    alocacao: bem?.alocacao || 'indireta',
    fornecedor: bem?.fornecedor || '',
    modelo: bem?.modelo || '',
    data_aquisicao: bem?.data_aquisicao?.slice(0, 10) || hojeIso(),
    valor_pago: parseFloat(bem?.valor_pago || '0') || 0,
    valor_residual: parseFloat(bem?.valor_residual || '0') || 0,
    percentual_dep: parseFloat(initialPctDep) || 0,
    vida_util_meses: bem?.vida_util_meses || 60,
    garantia_meses: bem?.garantia_meses || 0,
    observacoes: bem?.observacoes || '',
    custos_operacao: { ...(bem?.custos_operacao || {}) },
  });
  const [salvando, setSalvando] = useState(false);

  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  function calcResidualFromPct(valorPago: number, pctDep: number) {
    return +(valorPago * (1 - pctDep / 100)).toFixed(2);
  }
  function calcPctFromResidual(valorPago: number, residual: number) {
    if (!valorPago) return 0;
    return +((1 - residual / valorPago) * 100).toFixed(2);
  }

  function aplicarPreset(catLabel: string) {
    const p = CATEGORIAS_PRESET.find(c => c.label === catLabel);
    if (!p) { set('categoria', catLabel); return; }
    setForm((f: any) => ({
      ...f, categoria: catLabel, tipo: p.tipo, alocacao: p.alocacao,
      vida_util_meses: p.vida, percentual_dep: p.pctDep,
      valor_residual: f.valor_pago ? calcResidualFromPct(f.valor_pago, p.pctDep) : f.valor_residual,
    }));
  }

  function onChangeValorPago(v: number) {
    setForm((f: any) => ({
      ...f, valor_pago: v,
      valor_residual: v && f.percentual_dep ? calcResidualFromPct(v, f.percentual_dep) : f.valor_residual,
    }));
  }
  function onChangePctDep(pct: number) {
    setForm((f: any) => ({
      ...f, percentual_dep: pct,
      valor_residual: f.valor_pago ? calcResidualFromPct(f.valor_pago, pct) : f.valor_residual,
    }));
  }
  function onChangeResidual(res: number) {
    setForm((f: any) => ({
      ...f, valor_residual: res,
      percentual_dep: f.valor_pago ? calcPctFromResidual(f.valor_pago, res) : f.percentual_dep,
    }));
  }

  async function salvar() {
    if (!form.descricao || !form.data_aquisicao || !form.valor_pago || !form.vida_util_meses) {
      message.warning('Preencha todos os campos obrigatórios'); return;
    }
    setSalvando(true);
    try {
      const token = await getToken();
      const { percentual_dep, ...rest } = form;
      const payload = {
        ...rest,
        valor_pago: form.valor_pago,
        valor_residual: form.valor_residual || 0,
        vida_util_meses: parseInt(form.vida_util_meses),
        garantia_meses: parseInt(form.garantia_meses) || 0,
        custos_operacao: Object.fromEntries(Object.entries(form.custos_operacao || {}).filter(([, v]) => Number(v) > 0)),
      };
      if (bem) await axios.patch(`${API_URL}/patrimonio/${bem.id}`, payload, { headers: { Authorization: token } });
      else await axios.post(`${API_URL}/patrimonio`, payload, { headers: { Authorization: token } });
      message.success('Bem salvo.');
      onSaved();
    } catch (e: any) { message.error('Erro: ' + e.message); }
    finally { setSalvando(false); }
  }

  return (
    <Modal
      open onCancel={onClose} onOk={salvar} confirmLoading={salvando} width={700}
      title={bem ? 'Editar bem' : 'Novo bem no patrimônio'}
      okText={bem ? 'Salvar' : 'Criar'} cancelText="Cancelar" destroyOnClose
    >
      <Form layout="vertical" size="small">
        <Row gutter={10}>
          <Col span={8}>
            <Form.Item label="Código de patrimônio">
              <Input value={form.codigo_patrimonio} onChange={e => set('codigo_patrimonio', e.target.value)}
                placeholder="Ex.: 042 / CPU-2023" />
            </Form.Item>
          </Col>
          <Col span={16}>
            <Form.Item label="Descrição" required>
              <Input value={form.descricao} onChange={e => set('descricao', e.target.value)} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Categoria (preset)">
              <Select value={form.categoria} onChange={aplicarPreset}
                options={[
                  ...CATEGORIAS_PRESET.map(c => ({ label: `${c.label} (${c.tipo})`, value: c.label })),
                  { label: 'Outra', value: '' },
                ]} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Tipo">
              <Select value={form.tipo} onChange={(v) => set('tipo', v)}
                options={[
                  { label: 'Tangível (Depreciação)', value: 'tangivel' },
                  { label: 'Intangível (Amortização)', value: 'intangivel' },
                ]} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Alocação">
              <Select value={form.alocacao} onChange={(v) => set('alocacao', v)}
                options={[
                  { label: 'Direta (projeto/campo)', value: 'direta' },
                  { label: 'Indireta (escritório/admin)', value: 'indireta' },
                ]} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Fornecedor">
              <Input value={form.fornecedor} onChange={e => set('fornecedor', e.target.value)} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Modelo">
              <Input value={form.modelo} onChange={e => set('modelo', e.target.value)} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Data de aquisição" required>
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                value={form.data_aquisicao ? dayjs(form.data_aquisicao) : null}
                onChange={(d) => set('data_aquisicao', d ? d.format('YYYY-MM-DD') : '')} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Valor pago" required>
              <InputNumber {...PROPS_DINHEIRO_BR} value={form.valor_pago} onChange={(v) => onChangeValorPago(Number(v) || 0)}
                min={0} step={0.01} style={{ width: '100%' }} prefix="R$" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="% Depreciação"
              extra={form.percentual_dep ? `Residual: ${(100 - form.percentual_dep).toFixed(2)}%` : ''}>
              <InputNumber value={form.percentual_dep} onChange={(v) => onChangePctDep(Number(v) || 0)}
                min={0} max={100} step={0.01} style={{ width: '100%' }} suffix="%" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Valor residual (R$)" extra="Vinculado ao % acima">
              <InputNumber {...PROPS_DINHEIRO_BR} value={form.valor_residual} onChange={(v) => onChangeResidual(Number(v) || 0)}
                min={0} step={0.01} style={{ width: '100%' }} prefix="R$" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Vida útil (meses)" required
              extra={form.vida_util_meses ? `= ${Math.round(form.vida_util_meses / 12)} ano(s)` : ''}>
              <InputNumber value={form.vida_util_meses} onChange={(v) => set('vida_util_meses', Number(v) || 0)}
                min={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="Garantia (meses)">
              <InputNumber value={form.garantia_meses} onChange={(v) => set('garantia_meses', Number(v) || 0)}
                min={0} style={{ width: '100%' }} />
              {form.garantia_meses && form.data_aquisicao && (() => {
                const d = new Date(form.data_aquisicao);
                d.setMonth(d.getMonth() + parseInt(form.garantia_meses));
                const venceu = d < new Date();
                return (
                  <Text style={{ fontSize: 10, color: venceu ? '#B91626' : '#0a7f4a' }}>
                    {venceu ? `⚠ Vencida em ${fmtDate(d.toISOString())}` : `Válida até ${fmtDate(d.toISOString())}`}
                  </Text>
                );
              })()}
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Observações">
              <Input.TextArea value={form.observacoes} onChange={e => set('observacoes', e.target.value)} rows={2} />
            </Form.Item>
          </Col>
        </Row>

        {/* FECHAMENTO-CC F3: custos de operação → custo/dia que o contrato paga por dia de check-in */}
        <Card size="small" title={<span style={{ fontSize: 12 }}>Custos de operação (para o custo/dia por contrato)</span>}
          style={{ marginBottom: 12 }} bodyStyle={{ padding: 10 }}
          extra={<Text type="secondary" style={{ fontSize: 11 }}>opcional — vazio = só depreciação</Text>}>
          <Row gutter={8}>
            {CUSTOS_OPERACAO_CAMPOS.map(c => (
              <Col span={8} key={c.key}>
                <Form.Item label={<Tooltip title={c.dica}>{c.label} <Text type="secondary" style={{ fontSize: 10 }}>({c.sufixo})</Text></Tooltip>} style={{ marginBottom: 6 }}>
                  <InputNumber {...PROPS_DINHEIRO_BR} min={0} value={form.custos_operacao?.[c.key]} style={{ width: '100%' }}
                    onChange={(v) => setForm((f: any) => ({ ...f, custos_operacao: { ...(f.custos_operacao || {}), [c.key]: Number(v) || 0 } }))} />
                </Form.Item>
              </Col>
            ))}
          </Row>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Estes valores são provisão contra as linhas reais da DRE (manutenção, seguro…) — o motor do fechamento distribui
            por dia de uso e o que não teve gasto real no mês aparece como "encargos provisionados".
          </Text>
        </Card>

        {form.valor_pago && form.vida_util_meses && (() => {
          const depMes = (form.valor_pago - (form.valor_residual || 0)) / parseInt(form.vida_util_meses);
          const opMes = Object.entries(form.custos_operacao || {}).reduce((a, [k, v]) => a + ((Number(v) || 0) ? (k.endsWith('_pct_aa') ? form.valor_pago * Number(v) / 100 : Number(v)) / 12 : 0), 0);
          return (
            <Card size="small" style={{ background: '#F0F1F5' }} bodyStyle={{ padding: 8 }}>
              <Text style={{ fontSize: 11, color: '#555' }}>
                <strong>Depreciação mensal estimada:</strong> {fmt(depMes)}
                {opMes > 0 && <> · <strong>operação:</strong> {fmt(opMes)}/mês</>}
                {' '}· <strong>custo/dia (21 d.u.):</strong> {fmt((depMes + opMes) / 21)}
              </Text>
            </Card>
          );
        })()}

        {bem && <SecaoAnexos bemId={bem.id} />}
      </Form>
    </Modal>
  );
}

function ModalBaixar({ bem, onClose, onSaved }: { bem: Bem; onClose: () => void; onSaved: () => void }) {
  const { message } = AntApp.useApp();
  const [form, setForm] = useState({
    data_baixa: hojeIso(), motivo_baixa: 'venda', valor_venda: '',
  });
  const [salvando, setSalvando] = useState(false);

  async function confirmar() {
    setSalvando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/patrimonio/${bem.id}/baixar`, {
        ...form, valor_venda: parseFloat(form.valor_venda) || null,
      }, { headers: { Authorization: token } });
      message.success('Baixa registrada.');
      onSaved();
    } catch (e: any) { message.error('Erro: ' + e.message); }
    finally { setSalvando(false); }
  }

  return (
    <Modal
      open onCancel={onClose} onOk={confirmar} confirmLoading={salvando}
      title="Baixar bem"
      okText="Confirmar baixa" cancelText="Cancelar"
      okButtonProps={{ style: { background: '#B07500', borderColor: '#B07500' } }}
    >
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 14 }}>{bem.descricao}</Text>
      <Form layout="vertical" size="small">
        <Form.Item label="Data da baixa">
          <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
            value={form.data_baixa ? dayjs(form.data_baixa) : null}
            onChange={(d) => setForm({ ...form, data_baixa: d ? d.format('YYYY-MM-DD') : '' })} />
        </Form.Item>
        <Form.Item label="Motivo">
          <Select value={form.motivo_baixa} onChange={(v) => setForm({ ...form, motivo_baixa: v })}
            options={[
              { label: 'Venda', value: 'venda' }, { label: 'Descarte', value: 'descarte' },
              { label: 'Obsolescência', value: 'obsolescencia' }, { label: 'Perda/Roubo', value: 'perda' },
            ]} />
        </Form.Item>
        {form.motivo_baixa === 'venda' && (
          <Form.Item label="Valor de venda">
            <InputNumber {...PROPS_DINHEIRO_BR} value={parseFloat(form.valor_venda) || 0}
              onChange={(v) => setForm({ ...form, valor_venda: String(v ?? 0) })}
              min={0} step={0.01} style={{ width: '100%' }} prefix="R$" />
          </Form.Item>
        )}
      </Form>
      <Card size="small" style={{ background: '#FFF9F0', borderColor: '#F3D88B' }} bodyStyle={{ padding: 8 }}>
        <Text style={{ fontSize: 11, color: '#B07500' }}>
          ⚠ Depreciação cessa na data da baixa. O bem para de aparecer na DRE a partir daí.
        </Text>
      </Card>
    </Modal>
  );
}

export default function Patrimonio() {
  return (
    <AntApp>
      <PatrimonioInner />
    </AntApp>
  );
}
